import { IChecklistAnswer } from 'entities/IChecklistAnswer';
import * as gql from 'gql-query-builder';
import { InspectionCreateParams } from 'entities/GraphQL/InspectionCreateParams';
import { CreateInspectionResultInput } from 'entities/GraphQL/CreateInspectionResultInput';
import { CategoryResultPayloadStatus } from 'entities/attributes/InspectionCategoryResultPayloadJSONAttributes';
import { InspectionUpdateParams } from 'entities/GraphQL/InspectionUpdateParams';
import { UpdateInspectionResultInput } from 'entities/GraphQL/UpdateInspectionResultInput';
import { UpsertInspectionCategoryResultInput } from 'entities/GraphQL/UpsertInspectionCategoryResultInput';

import {
  inspectionCategoryResultsFields,
  inspectionFields,
} from './queryFields';

export const createInspectionMutation = (inspection: InspectionCreateParams) =>
  gql.mutation({
    operation: 'createInspection',
    variables: {
      inspection: {
        required: true,
        type: 'CreateInspectionInput',
        value: inspection,
      },
    },
    fields: ['id'],
  });

export const updateInspectionMutation = ({
  id,
  completedAt,
  modifiedBy,
  images,
}: InspectionUpdateParams) =>
  gql.mutation({
    operation: 'updateInspection',
    variables: {
      id: {
        required: true,
        value: id,
      },
      inspection: {
        required: true,
        type: 'UpdateInspectionInput',
        value: { completedAt, modifiedBy, images },
      },
    },
    fields: ['id', 'completedAt', 'modifiedBy'],
  });

export const updateInspectionImagesMutation = ({
  id,
  completedAt,
  modifiedBy,
  images,
}: InspectionUpdateParams) =>
  gql.mutation({
    operation: 'updateInspection',
    variables: {
      id: {
        required: true,
        value: id,
      },
      inspection: {
        required: true,
        type: 'UpdateInspectionInput',
        value: { completedAt, modifiedBy, images },
      },
    },
    fields: ['id', 'completedAt', 'modifiedBy', 'images'],
  });

export const inspectionResultCreateMutation = (
  inspectionResult: CreateInspectionResultInput
) =>
  gql.mutation({
    operation: 'inspectionResultCreate',
    variables: {
      inspectionResult: {
        required: true,
        type: 'CreateInspectionResultInput',
        value: inspectionResult,
      },
    },
    fields: ['id'],
  });

export const inspectionResultUpdateMutation = (
  inspectionResult: UpdateInspectionResultInput
) =>
  gql.mutation({
    operation: 'inspectionResultUpdate',
    variables: {
      inspectionResult: {
        required: true,
        type: 'UpdateInspectionResultInput',
        value: inspectionResult,
      },
    },
    fields: ['id'],
  });

export const inspectionResultRemoveMutation = (inspectionResultId: string) =>
  gql.mutation({
    operation: 'inspectionResultRemove',
    variables: {
      inspectionResultId: {
        required: true,
        type: 'String',
        value: inspectionResultId,
      },
    },
  });

export const inspectionResetMutation = (inspectionId: string) =>
  gql.mutation({
    operation: 'resetInspection',
    variables: {
      id: {
        required: true,
        type: 'String',
        value: inspectionId,
      },
    },
    fields: inspectionFields,
  });

export const categoriesResultSetStatusMutation = (
  inspectionId: string,
  categoryId: string,
  status: CategoryResultPayloadStatus
) =>
  gql.mutation({
    operation: 'categoriesResultSetStatus',
    variables: {
      inspectionId: {
        required: true,
        type: 'String',
        value: inspectionId,
      },
      categoryId: {
        required: true,
        type: 'String',
        value: categoryId,
      },
      status: {
        required: true,
        type: 'String',
        value: status,
      },
    },
    fields: inspectionCategoryResultsFields,
  });

export const categoriesResultRemoveStatusMutation = (
  inspectionId: string,
  categoryId: string
) =>
  gql.mutation({
    operation: 'categoriesResultRemoveStatus',
    variables: {
      inspectionId: {
        required: true,
        type: 'String',
        value: inspectionId,
      },
      categoryId: {
        required: true,
        type: 'String',
        value: categoryId,
      },
    },
    fields: inspectionCategoryResultsFields,
  });

export const categoryResultUpsertMutation = (
  categoryResult: UpsertInspectionCategoryResultInput
) =>
  gql.mutation({
    operation: 'categoryResultUpsert',
    variables: {
      categoryResult: {
        required: true,
        type: 'UpsertInspectionCategoryResultInput',
        value: categoryResult,
      },
    },
    fields: inspectionCategoryResultsFields,
  });

export const answerChecklistQuestions = (
  answers: Partial<IChecklistAnswer>[]
) =>
  gql.mutation({
    operation: 'answerChecklistQuestions',
    variables: {
      answers: {
        required: true,
        type: '[UpdateAnswerInput!]',
        value: answers,
      },
    },
    fields: ['id', 'answer'],
  });
